// Config → Training: edit tours (incl. AI drafts) and see who has completed / signed off.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, GraduationCap, RotateCcw } from "lucide-react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import TourEditor from "./TourEditor";
import { useTraining } from "../../components/training/TrainingContext";

const th = "px-3 py-2 text-left font-mono uppercase tracking-wider text-[9.5px] text-slate-400 whitespace-nowrap";
const td = "px-3 py-2 text-[12px] text-slate-300 whitespace-nowrap";

function ProgressTab() {
  const [d, setD] = useState(null);
  const { load: reloadMine } = useTraining();
  const load = useCallback(() => api.get("/training/progress").then(({ data }) => setD(data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const reset = async (r) => { if (!window.confirm(`Reset all training for ${r.name}?`)) return; await api.post("/training/reset", { user_id: r.user_id }); toast.success("Reset"); load(); reloadMine?.(); };
  return (
    <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="training-progress-table">
      <table className="w-full"><thead className="bg-white/[0.03]"><tr><th className={th}>Staff</th><th className={th}>Required done</th>
        {d.tours.map((t) => <th key={t.key} className={`${th.replace("whitespace-nowrap", "whitespace-normal")} max-w-[96px] leading-tight align-bottom`} title={t.title}>{(t.title || t.key).replace(/^Welcome to /, "")}</th>)}<th className={th}>Signed off</th><th className={th} /></tr></thead>
        <tbody>{d.rows.map((r) => (
          <tr key={r.user_id} className="border-t border-white/[0.05]" data-testid={`training-row-${r.user_id}`}>
            <td className={`${td} text-white font-medium`}>{r.name}</td><td className={td}>{r.done}/{r.required}</td>
            {d.tours.map((t) => <td key={t.key} className={td}>{r.completed[t.key] ? <CheckCircle2 className="w-4 h-4 text-teal-300" title={new Date(r.completed[t.key]).toLocaleString("en-GB")} /> : <span className="text-slate-600">·</span>}</td>)}
            <td className={td}>{r.signed_off_at ? <span className="text-teal-200">{new Date(r.signed_off_at).toLocaleDateString("en-GB")}</span> : <span className="text-slate-500">Not yet</span>}</td>
            <td className={td}><button type="button" onClick={() => reset(r)} className="inline-flex items-center gap-1 text-[11px] text-amber-200 hover:underline" data-testid={`training-reset-${r.user_id}`}><RotateCcw className="w-3 h-3" /> Reset</button></td>
          </tr>))}</tbody></table>
    </div>
  );
}

function ToursTab() {
  const [tours, setTours] = useState([]);
  const [params, setParams] = useSearchParams();
  const sel = params.get("edit");
  const load = useCallback(() => api.get("/training/me").then(({ data }) => setTours(data.tours)), []);
  useEffect(() => { load(); }, [load]);
  const cur = tours.find((t) => t.key === sel) || null;
  const add = async () => {
    const title = window.prompt("New tour title?"); if (!title) return;
    const key = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    await api.put(`/training/tours/${key}`, { title, summary: "", route: "", steps: [], required: false, status: "draft", order: 60 });
    await load(); setParams({ edit: key });
  };
  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-5">
      <div className="space-y-1.5" data-testid="training-tour-list">
        {tours.map((t) => (
          <button key={t.key} type="button" onClick={() => setParams({ edit: t.key })} data-testid={`training-edit-${t.key}`}
            className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${sel === t.key ? "border-teal-400/50 bg-teal-500/10" : "border-white/10 hover:bg-white/[0.03]"}`}>
            <div className="text-[12.5px] text-white font-medium">{t.title}</div>
            <div className="text-[10.5px] font-mono text-slate-400">{t.steps?.length || 0} steps · {t.status === "draft" ? "DRAFT" : t.required ? "required" : "optional"} · {t.route || "any page"}</div>
          </button>))}
        <button type="button" onClick={add} className="w-full rounded-lg border border-dashed border-white/15 px-3 py-2 text-[12px] text-slate-300 hover:bg-white/[0.03]" data-testid="training-new-tour">+ New tour</button>
        <p className="text-[11px] text-slate-500 pt-2">Tip: open any page, click ? → "AI draft a tour for this page" to get a first draft written from the page and the Hub.</p>
      </div>
      <div>{cur ? <TourEditor tour={cur} onSaved={(t) => setTours(tours.map((x) => (x.key === t.key ? t : x)))} onDeleted={() => { setParams({}); load(); }} />
        : <div className="text-slate-500 text-[13px] py-16 text-center">Pick a tour to edit.</div>}</div>
    </div>
  );
}

export default function TrainingAdmin() {
  const [tab, setTab] = useState("tours");
  return (
    <Layout>
      <div className="max-w-[1400px] space-y-5" data-testid="training-admin">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl grid place-items-center bg-teal-500/15 border border-teal-400/30"><GraduationCap className="w-5 h-5 text-teal-300" /></div>
          <div><h1 className="font-display text-2xl font-bold text-white">Training</h1><p className="text-[13px] text-slate-400">Edit the page tours staff see under the ? button, and track who has completed and signed off.</p></div>
        </div>
        <div className="flex gap-1 border-b border-white/10">{[["tours", "Tours"], ["progress", "Staff progress"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`training-tab-${k}`} className={`px-4 py-2 text-[13px] font-semibold border-b-2 -mb-px ${tab === k ? "border-teal-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>{l}</button>))}</div>
        {tab === "tours" ? <ToursTab /> : <ProgressTab />}
      </div>
    </Layout>
  );
}
